import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { useI18n } from "@/lib/i18n";
import { FadeUp, MaskedLine } from "@/components/Motion";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { Seo } from "@/components/Seo";

export default function Story() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div className="bg-black min-h-screen" data-testid="story-page">
      <Seo title={`${t.story.eyebrow} — Madubau Dolomiti`} description={t.seo.story_d} path="/story" />
      {/* HERO — Portfolio style: big serif left, arch photo right */}
      <section ref={ref} className="relative min-h-screen pt-40 lg:pt-48 pb-24 lg:pb-32 overflow-hidden">
        <BeeTileBackdrop opacity={0.4} />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — Title */}
          <div className="lg:col-span-7">
            <h1
              className="font-editorial italic font-light text-gold leading-[0.95] mb-10"
              style={{ fontSize: "clamp(3rem, 8vw, 8rem)" }}
            >
              {(t.story.hero_lines || ["Madubau", "Honig mit", "Herkunft &", "Herz."]).map((line, i) => (
                <MaskedLine key={i} delay={0.2 + i * 0.2}>{line}</MaskedLine>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="font-editorial italic text-gold/80 text-xl lg:text-2xl"
            >
              — Val Gardena · Dolomiti
            </motion.p>
          </div>

          {/* RIGHT — Arch photo */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end">
            <motion.figure style={{ y: imgY }} className="relative w-[280px] sm:w-[340px] lg:w-full max-w-[380px]">
              <div className="overflow-hidden clip-arch aspect-[3/5] bg-[#0d0d0d]">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/2fjt5cw2_WhatsApp%20Image%202026-07-27%20at%2016.47.09%281%29.webp"
                  alt="Der Imker"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption className="mt-10 text-center font-editorial italic text-cream/80 text-lg lg:text-xl leading-relaxed">
                {t.story.close.split("—")[0]}
                <br />
                <span className="text-gold">— {t.story.close.split("—")[1]?.trim()}</span>
              </figcaption>
              <div className="mt-8 text-center">
                <Link
                  to="/contact"
                  data-testid="story-contact-link"
                  className="font-display text-[11px] tracking-[0.4em] text-cream hover:text-gold underline underline-offset-8 decoration-cream/40 hover:decoration-gold"
                >
                  KONTAKT:
                </Link>
              </div>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* BODY — Editorial paragraphs */}
      <section className="relative pt-16 pb-32 lg:pb-40 overflow-hidden">
        <BeeTileBackdrop opacity={0.3} />
        <div className="relative max-w-2xl mx-auto px-6">
          <FadeUp className="mb-14">
            <div className="w-16 h-px bg-gold/60 mb-14 mx-auto" />
          </FadeUp>

          <div className="space-y-10 text-cream/80 font-body text-lg leading-[1.9]">
            <FadeUp>
              <p dangerouslySetInnerHTML={{ __html: t.origin.p1 }} />
            </FadeUp>
            <FadeUp>
              <p dangerouslySetInnerHTML={{ __html: t.origin.p2 }} />
            </FadeUp>
            <FadeUp>
              <p dangerouslySetInnerHTML={{ __html: t.origin.p3 }} />
            </FadeUp>
            {t.origin.p4 && (
              <FadeUp>
                <p dangerouslySetInnerHTML={{ __html: t.origin.p4 }} />
              </FadeUp>
            )}
          </div>

          <FadeUp className="mt-16 pl-6 border-l border-gold/40">
            <p className="font-editorial italic text-2xl lg:text-3xl text-gold leading-snug">
              {t.origin.pull}
            </p>
          </FadeUp>

          <FadeUp className="mt-14 flex items-center gap-4 font-display text-[10px] tracking-[0.35em] text-cream/60">
            <span className="w-8 h-px bg-cream/30" />
            <span>MADU · HONIG — BAU · FAMILIE</span>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
